import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { has } from "lodash";
import React, { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { ErrorText } from "../../components/billing";
import { Form } from "../../components/forms";
import { FileField } from "../../components/forms/file-2";
import { Select } from "../../components/forms/select";
import { useFileLoader } from "../../components/hooks/file-loader";
import { KycIcon } from "../../components/icons/status";
import { Grid as CustomGrid } from "../../components/layout";
import { Loader } from "../../components/loader";
import { useFetchable } from "../../store/common/fetchable";
import {
  useAddressAttachmentType,
  useIdentityAttachmentType,
} from "../../store/common/models";
import { UsersDTO } from "../../store/users/models/dto";
import { Users } from "../../store/users/reducer";
import { VerificationDTO } from "../../store/verification/models/dto/kyc";
import { VerificationService } from "../../store/verification/service";
import { deepOmitBy, mapObject } from "../../utils/utils";
import { CertificateTile } from "../dashboard/tiles/download-certificate";
import { KycStatusTile } from "../dashboard/tiles/kyc-status";
import { KycStatus, StyledWidget } from "../kyc-kyb-aml/components";
import { CTA, Section, StyledDateField, StyledTextField } from "./base";
import { unverifiableStatuses } from "./kyc-common";

type RequestDTO = VerificationDTO.Requests.UploadSelfKyc.T;
const { useFields, defaults } = VerificationDTO.Requests.UploadSelfKyc;

export const KycVerificationPage: React.FunctionComponent<{}> = () => {
  const userDetails = useFetchable(Users.current);

  return (
    <Loader data={[userDetails]}>
      {([actualUserDetails]) =>
        actualUserDetails.kycStatus === "VERIFIED" ? (
          <KycVerifiedWidget />
        ) : (
          <KycForm userDetails={actualUserDetails} />
        )
      }
    </Loader>
  );
};

const KycVerifiedWidget: React.FunctionComponent<{}> = () => (
  <CustomGrid col-xs={1} col-sm={1}>
    <KycStatusTile />
    {/* <CertificateTile /> */}
  </CustomGrid>
);

const KycForm: React.FunctionComponent<{
  userDetails: UsersDTO.Responses.GetSingle.T;
}> = ({ userDetails }) => {
  const { labels, validation } = useFields();

  const defaultValues = useMemo(
    () => ({
      ...defaults,
      ...deepOmitBy(
        VerificationDTO.Requests.UploadIndividualKyc.mapFromUser(userDetails),
        (x) => x === undefined || x === null
      ),
      fraudCheckId: v4(),
    }),
    [userDetails, defaults]
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RequestDTO>({
    defaultValues,
    resolver: yupResolver(validation),
  });

  const loader = useFileLoader();
  const router = useRouter();

  const handleSubmitCallback: (data: RequestDTO) => Promise<void> = useCallback(
    async (data) => {
      try {
        const result = await loader(VerificationService.postSelfKYC(data));

        if (result.status === "done") {
          router.stateService.reload();
        }
      } catch (e: any) {
        setValue("fraudCheckId", v4());
      }
    },
    [loader, router, setValue]
  );

  const addressAttachmentType = useAddressAttachmentType();
  const identityAttachmentType = useIdentityAttachmentType();

  const hasUserDataErrors = useMemo(() => has(errors, "userDetails"), [errors]);

  const hideCTA = useMemo(
    () => unverifiableStatuses.includes(userDetails.kycStatus),
    [userDetails]
  );

  return (
    <StyledWidget
      title="My KYC"
      adornment={
        userDetails.kycStatus ? (
          <KycStatus>{KycIcon(userDetails.kycStatus, "kyc")}</KycStatus>
        ) : null
      }
    >
      <Form onSubmit={handleSubmit(handleSubmitCallback)}>
        <Section>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="faceAttachment"
              render={({ field, fieldState }) => (
                <FileField
                  {...field}
                  label={labels.faceAttachment}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="identityDetails.attachment.type"
              render={({ field, fieldState }) => (
                <Select
                  fullWidth
                  label="Identity Attachment Type"
                  options={mapObject(
                    identityAttachmentType.enum,
                    (value, key) => ({
                      title: value,
                      value: key,
                    })
                  )}
                  {...field}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="identityDetails.attachment.file"
              render={({ field, fieldState }) => (
                <FileField
                  {...field}
                  label="Identity Attachment"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="identityDetails.additionalFile"
              render={({ field, fieldState }) => (
                <FileField
                  {...field}
                  label="Additional Identity Attachment"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Section>
        <Section>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="identityDetails.identity.number"
              render={({ field, fieldState }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Identity Document Number"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="identityDetails.identity.issueDate"
              render={({ field, fieldState }) => (
                <StyledDateField
                  {...field}
                  fullWidth
                  label="Issue Date"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name="identityDetails.identity.expiryDate"
              render={({ field, fieldState }) => (
                <StyledDateField
                  {...field}
                  fullWidth
                  label="Expiry Date"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Section>
        <Section>
          <Grid item xs={7}>
            <Controller
              control={control}
              name="addressAttachment.type"
              render={({ field, fieldState }) => (
                <Select
                  fullWidth
                  label="Address Attachment Type"
                  options={mapObject(
                    addressAttachmentType.enum,
                    (value, key) => ({
                      title: value,
                      value: key,
                    })
                  )}
                  {...field}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={5}>
            <Controller
              control={control}
              name="addressAttachment.file"
              render={({ field, fieldState }) => (
                <FileField
                  {...field}
                  label="Address Attachment"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Section>

        <CTA disabled={hasUserDataErrors || hideCTA}>Verify myself</CTA>
        {hasUserDataErrors ? (
          <ErrorText>Please update your Personal Information first</ErrorText>
        ) : null}
      </Form>
    </StyledWidget>
  );
};
